import throttle from 'lodash/throttle';

import {getElemHeight, insertAfter} from './dom';
import {bypassStickyStack} from './dom-sticky-stack';

/**
 * Wires up a sticky element that will be allowed to escape its parent. While scroll is
 * in the first half of the parent or above, position sticky is leveraged, and then it will
 * swap to position fixed in order to escape the parent container. The function returned
 * will allow for clean-up.
 *
 * This does a crude attempt at integrating with sticky stack for elements stacked directly under chrome header
 *
 * @param {HTMLElement} node
 * @param {number} stickyPoint
 */
export function stickyFixed(element: HTMLElement, stickyPoint: number) {
  let stickyStackCleanup = null;
  // It would be nice if we could check sticky stack for some type of keyed element
  if (document.querySelector<HTMLElement>(`mp-chrome-header`)?.offsetHeight === stickyPoint) {
    stickyStackCleanup = bypassStickyStack(
      // technically, the template of chrome header is stickied, not chrome header itself, so we need to peirce the shadow DOM
      // to get the element that is set to be sticky (this is because the sticky configuration is done via snabbdom hook)
      document.querySelector<HTMLElement>(`mp-chrome-header`).shadowRoot.lastElementChild as HTMLElement,
      element,
    );
  }

  element.classList.add(`is-sticky`);

  element.style.top = `${stickyPoint}px`;

  const placeHolderDiv = document.createElement(`div`);
  placeHolderDiv.id = `sticky-placeholder`;
  insertAfter(placeHolderDiv, element);

  let currentState = null;

  const setSticky = throttle(() => {
    if (element.getBoundingClientRect().top === stickyPoint) {
      element.classList.add(`stuck`);
    } else {
      element.classList.remove(`stuck`);
    }

    if (element.parentElement) {
      const parentTop = element.parentElement.getBoundingClientRect().top + window.scrollY;
      const parentHeight = getElemHeight(element.parentElement);

      const desiredState =
        window.scrollY > parentTop - stickyPoint + (parentHeight - stickyPoint) / 2 ? `fixed` : `sticky`;

      if (desiredState === `fixed` && desiredState !== currentState) {
        // we're about to flip the element to fixed position which will cause the DOM to reflow and "jump",
        // to counteract this, we'll adjust the placeholder consume all the space the element is currently
        // occupying. This will ensure a seamless transition.
        element.style.width = `${placeHolderDiv.getBoundingClientRect().width}px`;
        element.style.position = `fixed`;
        placeHolderDiv.style.height = `${getElemHeight(element)}px`;
        currentState = `fixed`;
      } else if (desiredState === `sticky` && desiredState !== currentState) {
        // if we've scroll up beyond half of the original parent container, revert the placeholder height,
        // switch the element from fixed to sticky, which will occupy all the space the placeholder  was taking up
        element.style.position = null;
        element.style.width = null;
        placeHolderDiv.style.height = null;
        currentState = `sticky`;
      }
    }
  }, 10);

  const rafSetSticky = () => {
    requestAnimationFrame(setSticky);
  };

  rafSetSticky();

  window.addEventListener(`scroll`, rafSetSticky);

  return () => {
    if (stickyStackCleanup) {
      stickyStackCleanup();
    }
    window.removeEventListener(`scroll`, rafSetSticky);
    window.requestAnimationFrame(() => {
      element.parentElement?.removeChild(placeHolderDiv);
      element.style.position = null;
      element.style.top = null;
      element.style.width = null;
      element.classList.remove(`stuck`);
      element.classList.remove(`is-sticky`);
    });
  };
}
